<template>
  <ItemRow
    v-bind="$attrs"
    :selected="itemData.selected"
    v-on="$listeners"
  >
    <ItemWithLabel
      :column="columns[0]"
    >
      <PasswordManagerUsernameLink
        :id="itemData.id"
        :userFullName="itemData.name"
        :username="itemData.username"
      />
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[1]"
    >
      {{ itemData.email }}
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[2]"
    >
      {{ itemData.itemsCount }}
    </ItemWithLabel>
    <ItemWithLabel
      class="groupsColumn"
      :column="columns[3]"
    >
      <span
        v-if="hasGroup"
        :data-test-id="$testId('firstGroupSpan')"
      >{{ itemData.groups[0].name }}</span>
      <span
        v-if="showTooltip"
        :data-test-id="$testId('moreGroupsSpan')"
      >
        <span>, </span>
        <Tooltip
          orientation="bottom"
          :text="groupNames"
        >
          <span class="linkText">more</span>
        </Tooltip>
      </span>
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[4]"
    >
      <PasswordManagerUserStatus :status="status" />
    </ItemWithLabel>
    <ItemWithLabel
      class="visibleOverflow"
      :column="columns[5]"
    >
      <PasswordManagerPasswordScoreStatus
        class="passwordManagerPasswordScoreStatus"
        :hasPasswords="!!itemData.passwordsCount"
        :passwordScore="itemData.passwordsScore"
        :scoreUpdatedAt="itemData.scoreUpdatedAt"
      />
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[6]"
    >
      {{ itemData.weakPasswords }}
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[7]"
    >
      {{ itemData.oldPasswords }}
    </ItemWithLabel>
    <ItemWithLabel
      :class="['actionsColumn', 'visibleOverflow']"
      :column="columns[8]"
      :data-test-id="$testId('actionsColumn')"
    >
      <DropdownActions
        buttonText=""
        class="drowpdowActions"
        :items="visibleDropdownItems"
        position="right"
      >
        <template v-slot:button="dropdown">
          <IconButton
            ariaLabel="actions"
            hasNoBackground
            icon="more"
            @click="dropdown.defaults.click()"
          />
        </template>
      </DropdownActions>
    </ItemWithLabel>
    <PasswordManagerUnenrollUserModal
      v-if="isUnenrollUserModalVisible"
      :groups="itemData.groups"
      @close="handleCloseUnenrollUserModal"
    />
  </ItemRow>
</template>

<script>
import {
  DropdownActions,
  IconButton,
  ItemRow,
  ItemWithLabel,
  Tooltip,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { passwordManagerUserStatus as passwordManagerUserStatusConsts } from 'client/PasswordManager/PasswordManagerConstants';
import PasswordManagerPasswordScoreStatus from 'client/PasswordManager/components/PasswordManagerPasswordScoreStatus.vue';
import PasswordManagerUnenrollUserModal from 'client/PasswordManager/Users/PasswordManagerUnenrollUserModal.vue';
import PasswordManagerUserStatus from 'client/PasswordManager/Users/PasswordManagerUserStatus.vue';
import PasswordManagerUsernameLink from 'client/PasswordManager/components/PasswordManagerUsernameLink.vue';

const { DropdownMenuItem } = Types;

const PasswordManagerUserCollection = 'PasswordManagerUserCollection';

export default {
  name: 'PasswordManagerUsersItemListRow',

  components: {
    DropdownActions,
    IconButton,
    ItemRow,
    ItemWithLabel,
    PasswordManagerPasswordScoreStatus,
    PasswordManagerUnenrollUserModal,
    PasswordManagerUsernameLink,
    PasswordManagerUserStatus,
    Tooltip,
  },

  props: {
    columns: {
      type: Array,
      default: () => ([]),
    },

    itemData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      dropdownItems: [
        DropdownMenuItem({
          click: this.showUnenrollUserModal,
          text: 'Revoke Access',
        })],
      isUnenrollUserModalVisible: false,
    };
  },

  computed: {
    ...mapGetters(PasswordManagerUserCollection, [
      'sentInvitationEmails',
    ]),

    groupNames() {
      return this.itemData.groups.slice(1)?.map(group => group.name).join(', ');
    },

    hasGroup() {
      return this.itemData.groups.length > 0;
    },

    isInvitationEmailSent() {
      return this.sentInvitationEmails[this.itemData.id];
    },

    showTooltip() {
      return this.itemData.groups.length > 1;
    },

    status() {
      return this.isInvitationEmailSent
        ? passwordManagerUserStatusConsts.invitationResent
        : this.itemData.status;
    },

    visibleDropdownItems() {
      if (this.itemData.status === passwordManagerUserStatusConsts.pending
      && !this.isInvitationEmailSent) {
        return [...this.dropdownItems, DropdownMenuItem({
          click: this.handleResendInvitation,
          text: 'Send Invitation Email',
        })];
      }

      return this.dropdownItems;
    },
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(PasswordManagerUserCollection, [
      'resendInvitation',
    ]),

    handleCloseUnenrollUserModal() {
      this.isUnenrollUserModalVisible = false;
    },

    async handleResendInvitation() {
      try {
        await this.resendInvitation([this.itemData.id]);

        this.triggerSuccessNotification({
          title: 'Success',
          message: `Invitation sent to ${this.itemData.name}.`,
        });
      } catch (error) {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'Failed to invite user. Please try again later.',
        });
      }
    },

    showUnenrollUserModal() {
      this.isUnenrollUserModalVisible = true;
    },
  },
};
</script>

<style scoped>
.actionsColumn.actionsColumn {
  display: flex;
  justify-content: flex-end;
}

.drowpdowActions.drowpdowActions {
  --dropdown-menu-width: 12.5rem;

  z-index: auto;
}

.groupsColumn.groupsColumn {
  overflow: visible;
}

.linkText {
  color: var(--jc-color-informational-strong);
  font-weight: 600;
}

.linkText:hover {
  cursor: pointer;
  text-decoration: underline;
}

.visibleOverflow {
  overflow: visible;
  text-overflow: unset;
}
</style>
